import { sortByFractionalOrder } from 'helpers/sort';
import { assignDepthAndNumbering } from './numbering';
import {
  ChecklistItemTreeData,
  ChecklistSectionTreeData,
  FlatTreeNode,
  ItemTreeNode,
  SectionTreeNode,
  TreeNode,
} from './types';

export function buildFlatNodes(
  sections: ChecklistSectionTreeData[],
  items: ChecklistItemTreeData[]
): FlatTreeNode[] {
  const sectionNodes: FlatTreeNode[] = sections.map((section) => ({
    id: section._id,
    type: 'section',
    order: section.order,
    parentSectionId: section.parentSectionId,
    data: section,
  }));

  const itemNodes: FlatTreeNode[] = items.map((item) => ({
    id: item._id,
    type: 'item',
    order: item.order,
    parentSectionId: item.parentSectionId,
    data: item,
  }));

  return [...sectionNodes, ...itemNodes];
}

export function buildTree(
  sections: ChecklistSectionTreeData[],
  items: ChecklistItemTreeData[]
): TreeNode[] {
  const flatNodes = buildFlatNodes(sections, items);

  const nodeMap = new Map<string, TreeNode>();
  const rootNodes: TreeNode[] = [];

  flatNodes.forEach((flatNode) => {
    const treeNode: TreeNode =
      flatNode.type === 'section'
        ? createSectionNode(flatNode.data)
        : createItemNode(flatNode.data);

    nodeMap.set(flatNode.id, treeNode);
  });

  flatNodes.forEach((flatNode) => {
    const node = nodeMap.get(flatNode.id);
    if (!node) {
      return;
    }

    if (flatNode.parentSectionId) {
      const parentNode = nodeMap.get(flatNode.parentSectionId);
      if (parentNode && parentNode.type === 'section') {
        parentNode.children.push(node);
      } else {
        rootNodes.push(node);
      }
    } else {
      rootNodes.push(node);
    }
  });

  sortTreeNodesRecursively(rootNodes);
  assignDepthAndNumbering(rootNodes, 0, '');

  return rootNodes;
}

function createSectionNode(section: ChecklistSectionTreeData): SectionTreeNode {
  return {
    id: section._id,
    type: 'section',
    order: section.order,
    parentSectionId: section.parentSectionId,
    depth: 0,
    displayNumber: '',
    children: [],
    data: section,
  };
}

function createItemNode(item: ChecklistItemTreeData): ItemTreeNode {
  return {
    id: item._id,
    type: 'item',
    order: item.order,
    parentSectionId: item.parentSectionId,
    depth: 0,
    displayNumber: '',
    children: [],
    data: item,
  };
}

function sortTreeNodesRecursively(nodes: TreeNode[]): void {
  nodes.sort(sortByFractionalOrder);
  nodes.forEach((node) => {
    if (node.type === 'section' && node.children.length > 0) {
      sortTreeNodesRecursively(node.children);
    }
  });
}
